import { MouseEvent, useEffect, useState } from "react";
import { IconButton, Popover, styled, SwipeableDrawer, useMediaQuery } from "@mui/material";
import { getEventName } from "../../pages/EcoBank/LatestTransaction";
import { Text } from "../Typography";
import moment from "moment/moment";
import { EtTransaction } from "../../types/LandToken";
import useMetaMask from "../../contexts/useMetaMask";
import { CURRENCY_NAME, DATE_TIME_FORMAT, EtTransactionType } from "../../utils/constants";
import { AddressLink, CurrencyLabel, CopyToClipboard } from "../";
import { consoleError, toReadable } from "../../utils/methods";
import { formatValue } from "../../utils/formatValue";
import { CheckCircleRounded, Close, InfoRounded } from "@mui/icons-material";
import FlagIcon from "@mui/icons-material/Flag";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { ecoFuturesBankContractAddress, treeContractAddress } from "../../services/contract.service";

type Props = {
  transaction: EtTransaction;
  simple?: boolean;
  flag?: boolean;
  time?: boolean;
  onClose?: () => void;
};

export const DetailsPopup = ({ transaction, simple, flag, time, onClose }: Props) => {
  const { library } = useMetaMask();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down("md"));

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onClose?.();
  };
  const { data } = transaction;
  const {
    guardianAmount,
    guardianReward,
    trustAmount,
    trustReward,
    ecoFutureAmount,
    ecoFutureReward,
    guardian,
    trust,
    ecoFuture,
  } = data || {};

  const [gasPrice, setGasPrice] = useState("-");
  const [txnFee, setTxnFee] = useState("-");

  const getGas = async (hash: string) => {
    try {
      if (hash) {
        // Use ethers BrowserProvider with window.ethereum from MetaMask SDK
        if (window.ethereum) {
          const { ethers } = await import("ethers");
          const provider = new ethers.BrowserProvider(window.ethereum);
          const receipt = await provider.getTransactionReceipt(hash);

          if (receipt && receipt.gasUsed && receipt.gasPrice) {
            const gasUsed = receipt.gasUsed;
            const effectiveGasPrice = receipt.gasPrice;
            // BigInt multiplication instead of mul method
            const fee = gasUsed * effectiveGasPrice;

            // Convert BigInt values to strings for formatting
            const gasPriceStr = effectiveGasPrice.toString();
            const feeStr = fee.toString();

            // Format values for display using ethers utility functions directly
            // This is more straightforward than using the formatValue function
            const { ethers } = await import("ethers");

            // Format the values using ethers formatting utilities
            setGasPrice(`${ethers.formatUnits(gasPriceStr, "gwei")} Gwei`);
            setTxnFee(`${ethers.formatEther(feeStr)} ETH`);
          }
        }
      }
    } catch (error: any) {
      consoleError("Failed to get receipt", { error });
    }
  };

  useEffect(() => {
    if (anchorEl) {
      getGas(transaction.hash);
    }
  }, [anchorEl]);

  const getValue = () => {
    if (transaction.type === EtTransactionType.CLAIM_FUNDS) {
      return (
        <>
          {(guardianAmount || guardianReward) && (
            <Text numeric sx={{ gap: "3px", flexWrap: "wrap" }}>
              {guardian ? (
                <AddressLink address={guardian} type={"user"} long small />
              ) : (
                <>
                  <span style={{ width: 90 }}>Guardian</span> received{" "}
                </>
              )}
              {toReadable(guardianReward || guardianAmount, true, true, 2)}{" "}
              <CurrencyLabel sx={{ fontSize: "inherit !important" }} currency={CURRENCY_NAME} />
            </Text>
          )}
          {(trustReward || trustAmount) && (
            <Text numeric sx={{ gap: "3px", flexWrap: "wrap" }}>
              {trust ? (
                <AddressLink address={trust} type={"trust"} long small />
              ) : (
                <>
                  <span style={{ width: 90 }}>Trust</span> received{" "}
                </>
              )}
              {toReadable(trustReward || trustAmount, true, true, 2)}{" "}
              <CurrencyLabel sx={{ fontSize: "inherit !important" }} currency={CURRENCY_NAME} />
            </Text>
          )}
          {(ecoFutureReward || ecoFutureAmount) && (
            <Text numeric sx={{ gap: "3px", flexWrap: "wrap" }}>
              {ecoFuture ? (
                <AddressLink address={ecoFuture} type={"user"} long small />
              ) : (
                <>
                  <span style={{ width: 90 }}>EcoFuture</span> received{" "}
                </>
              )}
              {toReadable(ecoFutureReward || ecoFutureAmount, true, true, 2)}{" "}
              <CurrencyLabel sx={{ fontSize: "inherit !important" }} currency={CURRENCY_NAME} />
            </Text>
          )}
        </>
      );
    }
    if (transaction.type === EtTransactionType.REWARD_CLAIMED) {
      if (data?.isPatron) {
        return (
          <Text numeric sx={{ flexWrap: "wrap" }}>
            <AddressLink address={data.patron} type="user" long sx={{ mr: 3 }} /> received{" "}
            {toReadable(data.patronReward, true, true, 3)}
            <CurrencyLabel sx={{ fontSize: "inherit !important" }} />
          </Text>
        );
      } else {
        return (
          <>
            <Text numeric sx={{ flexWrap: "wrap" }}>
              <AddressLink address={data.guardian} type="user" long sx={{ mr: 3 }} /> received{" "}
              {toReadable(data.guardianReward, true, true, 3)}
              <CurrencyLabel sx={{ fontSize: "inherit !important" }} />
            </Text>
            <Text numeric sx={{ flexWrap: "wrap" }}>
              <AddressLink address={data.referralAddress} type="user" long sx={{ mr: 3 }} /> received{" "}
              {toReadable(data.referralReward, true, true, 3)}
              <CurrencyLabel sx={{ fontSize: "inherit !important" }} />
            </Text>
          </>
        );
      }
    }
    if (transaction.type === EtTransactionType.UPDATED || transaction.type === EtTransactionType.MINTED) {
      return (transaction.etNft.ecoToken.ecoScore * 100).toFixed() + " Score";
    }
    if (transaction.type === EtTransactionType.TRUST_FLAGGED_FOR_DISMISSAL) {
      return transaction?.data?.reason || transaction?.reason || "No reason provided";
    }
    const value = data?.amount || data?.endingPrice || data?.startingBid || data?.startingPrice || transaction.txValue;
    return value ? formatValue(value, CURRENCY_NAME) : "";
  };

  const getSender = () => {
    if (transaction.type === EtTransactionType.REWARD_CLAIMED) {
      // if (data.isPatron) {
      //   return <AddressLink long address={data.patron} type="user" />;
      // } else {
      //   return <AddressLink long address={data.guardian} type="user" />;
      // }
      return <AddressLink long address={treeContractAddress} type="plain" />;
    }
    if (transaction.type === EtTransactionType.CLAIM_FUNDS) {
      return <AddressLink long address={ecoFuturesBankContractAddress} type="plain" />;
    }
    if (transaction.type === EtTransactionType.UPDATED) {
      return <AddressLink long address={data.trust} type="trust" />;
    }
    if (transaction.type === EtTransactionType.MINTED) {
      return <AddressLink long address={transaction.from as string} type="trust" />;
    }
    return transaction.from && <AddressLink long address={transaction.from} type="user" />;
  };

  const getReceiver = () => {
    if (transaction.type === EtTransactionType.UPDATED) {
      return <AddressLink long address={data.guardian} type="user" />;
    }
    if (transaction.type === EtTransactionType.MINTED) {
      return <AddressLink long address={transaction.to as string} type="user" />;
    }
    return transaction.to && <AddressLink long address={transaction.to} type="user" />;
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popup" : undefined;

  const hash = "0x" + transaction.hash?.slice(2).toUpperCase();

  return (
    <Wrap>
      {simple ? (
        <IconButton aria-describedby={id} onClick={handleClick}>
          <InfoRounded fontSize="small" color="disabled" />
        </IconButton>
      ) : flag ? (
        <button style={{ all: "unset" }} aria-describedby={id} onClick={handleClick}>
          <FlagIcon
            sx={{
              color: "black",
              fontSize: "1.2rem",
            }}
          />
        </button>
      ) : time ? (
        <button style={{ all: "unset" }} aria-describedby={id} onClick={handleClick}>
          <ArrowOutwardIcon
            sx={{
              color: "black",
              fontSize: "0.9rem",
            }}
          />
        </button>
      ) : (
        <HashText aria-describedby={id} onClick={handleClick}>
          {hash}
        </HashText>
      )}
      {(() => {
        const body = (
          <PopupBody>
            <div
              style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}
            >
              <Text bold medium sx={{ mb: 6 }}>
                Additional Info
              </Text>
              <IconButton size="small" onClick={handleClose}>
                <Close />
              </IconButton>
            </div>
            <PopupRow>
              <PopupRowCell>Transaction Hash</PopupRowCell>
              <PopupRowCell sx={{ wordBreak: "break-all", textTransform: "none !important" }}>
                {hash}
                <CopyToClipboard text={hash} />
              </PopupRowCell>
            </PopupRow>
            <PopupRow>
              <PopupRowCell>Type</PopupRowCell>
              <PopupRowCell>
                <Text capital>{getEventName(transaction as any)}</Text>
              </PopupRowCell>
            </PopupRow>
            <PopupRow>
              <PopupRowCell>Status</PopupRowCell>
              <PopupRowCell sx={{ alignItems: "center" }}>
                <CheckCircleRounded fontSize="small" color="success" />{" "}
                <Text capital sx={{ color: "success.main" }}>
                  Success
                </Text>
              </PopupRowCell>
            </PopupRow>
            <PopupRow>
              <PopupRowCell>EcoFuture ID</PopupRowCell>
              <PopupRowCell>{transaction.nftId}</PopupRowCell>
            </PopupRow>
            <PopupRow>
              <PopupRowCell>Timestamp</PopupRowCell>
              <PopupRowCell>
                <span>{moment(transaction.createdAt).fromNow()}</span>
                <span>( {moment(transaction.createdAt).format(DATE_TIME_FORMAT + ":ss")} )</span>
              </PopupRowCell>
            </PopupRow>
            <PopupRow>
              <PopupRowCell>From</PopupRowCell>
              <PopupRowCell>{getSender() || "-"}</PopupRowCell>
            </PopupRow>
            <PopupRow>
              <PopupRowCell>To</PopupRowCell>
              <PopupRowCell>{getReceiver() || "-"}</PopupRowCell>
            </PopupRow>
            <PopupRow>
              <PopupRowCell>Value</PopupRowCell>
              <PopupRowCell>
                <Text numeric sx={{ display: "flex", flexDirection: "column" }}>
                  {getValue() || "-"}
                </Text>
              </PopupRowCell>
            </PopupRow>
            {library && (
              <>
                <PopupRow>
                  <PopupRowCell>Transaction Fee</PopupRowCell>
                  <PopupRowCell>
                    <Text numeric>{txnFee}</Text>
                  </PopupRowCell>
                </PopupRow>
                <PopupRow>
                  <PopupRowCell>Gas Price</PopupRowCell>
                  <PopupRowCell>
                    <Text numeric>{gasPrice}</Text>
                  </PopupRowCell>
                </PopupRow>
              </>
            )}
          </PopupBody>
        );

        if (isMobile) {
          return (
            <StyledDrawer
              anchor="bottom"
              open={open}
              onClose={handleClose}
              onOpen={() => {}}
              disableSwipeToOpen={true}
              swipeAreaWidth={0}
              ModalProps={{ keepMounted: true }}
              slotProps={{
                backdrop: {
                  sx: {
                    backdropFilter: "blur(8px)",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                  },
                },
              }}
            >
              <DrawerHandle />
              {body}
            </StyledDrawer>
          );
        }

        return (
          <Popover
            classes={{ paper: "PopoverPaper" }}
            onClose={handleClose}
            id={id}
            open={open}
            disableScrollLock={true}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            {body}
          </Popover>
        );
      })()}
    </Wrap>
  );
};

const Wrap = styled("div")`
  .PopoverPaper {
    //padding: 24px;
  }
`;

const HashText = styled("div")`
  width: 130px;
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;
  font-family: IBM Mono, monospace;
  color: ${({ theme }) => theme.palette.text.primary};
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  &:hover {
    color: ${({ theme }) => theme.palette.text.disabled};
  }
  @media (max-width: 992px) {
    font-size: 14px;
  }
`;

const PopupBody = styled("div")`
  display: flex;
  flex-direction: column;
  padding: 12px;
`;

const StyledDrawer = styled(SwipeableDrawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    background: theme.palette.background.paper,
    borderRadius: "18px 18px 0 0",
    border: "none",
    boxShadow: "0 -4px 24px rgba(0, 0, 0, 0.3)",
    maxHeight: "90vh",
    paddingBottom: "env(safe-area-inset-bottom)",
  },
}));

const DrawerHandle = styled("div")(({ theme }) => ({
  width: 40,
  height: 4,
  borderRadius: 2,
  background: theme.palette.mode === "light" ? "#d0d0d0" : "#555",
  margin: "8px auto 0",
}));

export const PopupRow = styled("div")`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.default};

  padding-bottom: 12px;
  margin-bottom: 12px;

  &:last-of-type {
    margin-bottom: 0;
    border-bottom: 0;
    padding-bottom: 0;
  }

  & > div:first-of-type {
    font-weight: 500;
  }
  & > div:last-of-type {
    margin-top: 6px;
    font-family: IBM Mono, monospace;
    color: ${({ theme }) => (theme.palette.mode === "light" ? "#6c6c6c" : "#ccc")};
    text-transform: capitalize;
  }
`;

export const PopupRowCell = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
`;
